import { Link } from "react-router-dom";
import { ArrowUpRight, BookOpen, Cpu, GraduationCap, Coins, Layers, Sparkles, Atom } from "lucide-react";
import { useLightbox } from "@/components/LogoLightbox";

const SKILLARI_IMG =
  "https://images.unsplash.com/photo-1774360502057-a934d7d0ce60?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600";

const OMNIA_IMG =
  "https://images.unsplash.com/photo-1764553230197-ca14bbc82b8c?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600";

const SKILLARI_LOGO =
  "https://customer-assets.emergentagent.com/job_house-ihm-launch/artifacts/6tnu2ec7_IMG_2047.png";

const OMNIA_LOGO =
  "https://customer-assets.emergentagent.com/job_house-ihm-launch/artifacts/9zhcqzsi_bfc57ff6-48a4-45fd-89a1-5954e4b842c3.jpeg";

const SKILLARI_POINTS = [
  { icon: GraduationCap, title: "Student–Teacher Marketplace", desc: "Match learners with the right educators across subjects, cities and skill levels." },
  { icon: Layers, title: "Teaching Infrastructure", desc: "Tools to manage schedules, content, payments and student journeys end-to-end." },
  { icon: Coins, title: "Learning + Earning Ecosystem", desc: "An economy where teachers grow their practice while learners unlock real outcomes." },
];

const OMNIA_POINTS = [
  { icon: Cpu, title: "Wearable Technology", desc: "Intelligent, ambient hardware designed to integrate naturally with everyday life." },
  { icon: Atom, title: "AI-driven Systems", desc: "Conceptual frameworks blending sensing, on-device intelligence, and adaptive UX." },
  { icon: Sparkles, title: "Future-focused Innovation", desc: "Long-horizon R&D — built for the decade ahead, not the next quarter." },
];

export default function Ventures() {
  const { open } = useLightbox();
  return (
    <div data-testid="page-ventures" className="ihm-page-enter">
      {/* Hero */}
      <section>
        <div className="max-w-7xl mx-auto px-6 md:px-12 pt-24 md:pt-32 pb-16">
          <span className="ihm-chip">Our Ventures</span>
          <h1
            data-testid="ventures-title"
            className="mt-8 font-heading font-bold text-5xl md:text-7xl lg:text-[80px] tracking-tighter leading-[1.02] max-w-5xl"
          >
            Two ventures, each built to{" "}
            <span className="text-[#2997FF]">redefine its category</span>.
          </h1>
          <p className="mt-8 max-w-2xl text-[18px] text-[#6E6E73] leading-relaxed">
            From a two-sided EdTech marketplace to next-generation wearables, the
            ventures under House of IHM share one common thread — they are
            engineered to scale.
          </p>
        </div>
      </section>

      {/* SKILLARI */}
      <section
        id="skillari"
        data-testid="venture-section-skillari"
        className="border-t border-[#E5E5EA]"
      >
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-24 md:py-32">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-center">
            <div className="lg:col-span-6 order-2 lg:order-1">
              <div className="flex items-center gap-3">
                <span className="ihm-chip">
                  <BookOpen size={12} /> Venture · 01
                </span>
              </div>

              <button
                type="button"
                data-testid="skillari-logo"
                onClick={() => open(SKILLARI_LOGO, "Skillari logo")}
                aria-label="View Skillari logo fullscreen"
                className="mt-8 inline-flex items-center justify-center h-20 w-20 md:h-24 md:w-24 rounded-2xl bg-[#0E0E10] border border-[#1D1D1F] overflow-hidden cursor-zoom-in transition-transform duration-500 hover:scale-[1.05] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#2997FF] focus-visible:ring-offset-2"
              >
                <img
                  src={SKILLARI_LOGO}
                  alt="Skillari logo"
                  className="h-full w-full object-cover"
                  loading="lazy"
                />
              </button>

              <h2 className="mt-6 font-heading font-bold text-5xl md:text-6xl tracking-tighter leading-[1.05]">
                Skillari
              </h2>
              <p className="mt-6 text-[18px] md:text-[20px] leading-relaxed text-[#1D1D1F]">
                Skillari is a two-sided EdTech platform connecting students and
                educators, enabling learning, earning, and growth in one unified
                ecosystem.
              </p>
              <p className="mt-5 text-[16px] leading-relaxed text-[#6E6E73]">
                The platform focuses on accessibility, scalability, and
                empowering individuals to both learn and earn.
              </p>
            </div>

            <div className="lg:col-span-6 order-1 lg:order-2">
              <div className="relative aspect-[5/4] w-full overflow-hidden rounded-3xl bg-[#F5F5F7]">
                <img
                  src={SKILLARI_IMG}
                  alt="Skillari abstract visual"
                  className="h-full w-full object-cover"
                  loading="lazy"
                />
                <div className="absolute bottom-4 left-4 ihm-chip bg-white/90">
                  Two-sided · Marketplace
                </div>
              </div>
            </div>
          </div>

          {/* Key points */}
          <div className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-6">
            {SKILLARI_POINTS.map((p, i) => {
              const Icon = p.icon;
              return (
                <div
                  key={p.title}
                  data-testid={`skillari-point-${i}`}
                  className="ihm-card p-8"
                >
                  <Icon size={22} className="text-[#2997FF]" />
                  <h4 className="mt-5 font-heading font-semibold text-xl tracking-tight">
                    {p.title}
                  </h4>
                  <p className="mt-3 text-[14px] leading-relaxed text-[#6E6E73]">
                    {p.desc}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* OMNIA — dark accent block */}
      <section
        id="omnia"
        data-testid="venture-section-omnia"
        className="bg-[#1D1D1F] text-white"
      >
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-24 md:py-32">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-center">
            <div className="lg:col-span-6 order-2 lg:order-1">
              <div className="flex items-center gap-3">
                <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-white/20 bg-white/5 backdrop-blur-md text-[11px] uppercase font-mono tracking-[0.2em] text-white/80">
                  <Cpu size={12} /> Venture · 02
                </span>
              </div>

              <button
                type="button"
                data-testid="omnia-logo"
                onClick={() => open(OMNIA_LOGO, "Omnia logo")}
                aria-label="View Omnia logo fullscreen"
                className="mt-8 inline-flex items-center justify-center h-20 w-20 md:h-24 md:w-24 rounded-2xl border border-white/15 bg-white overflow-hidden cursor-zoom-in transition-transform duration-500 hover:scale-[1.05] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#2997FF] focus-visible:ring-offset-2"
              >
                <img
                  src={OMNIA_LOGO}
                  alt="Omnia logo"
                  className="h-full w-full object-cover"
                  loading="lazy"
                />
              </button>

              <h2 className="mt-6 font-heading font-bold text-5xl md:text-6xl tracking-tighter leading-[1.05]">
                Omnia
              </h2>
              <p className="mt-6 text-[18px] md:text-[20px] leading-relaxed text-white/90">
                Omnia is a next-generation technology initiative focused on AI
                systems, wearable intelligence, and future-facing innovation.
              </p>
              <p className="mt-5 text-[16px] leading-relaxed text-white/60">
                Built to explore the next frontier of ambient, intelligent
                hardware — engineered for the decade ahead.
              </p>
            </div>

            <div className="lg:col-span-6 order-1 lg:order-2">
              <div className="relative aspect-[5/4] w-full overflow-hidden rounded-3xl bg-black">
                <img
                  src={OMNIA_IMG}
                  alt="Omnia metallic visual"
                  className="h-full w-full object-cover opacity-90"
                  loading="lazy"
                />
                <div className="absolute bottom-4 left-4 inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-white/20 bg-white/10 backdrop-blur-md text-[11px] uppercase font-mono tracking-[0.2em] text-white/90">
                  <Cpu size={12} /> Wearable Intelligence
                </div>
              </div>
            </div>
          </div>

          {/* Key points dark */}
          <div className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-6">
            {OMNIA_POINTS.map((p, i) => {
              const Icon = p.icon;
              return (
                <div
                  key={p.title}
                  data-testid={`omnia-point-${i}`}
                  className="rounded-3xl border border-white/10 bg-white/[0.04] p-8 transition-colors duration-500 hover:bg-white/[0.07]"
                >
                  <Icon size={22} className="text-[#2997FF]" />
                  <h4 className="mt-5 font-heading font-semibold text-xl tracking-tight text-white">
                    {p.title}
                  </h4>
                  <p className="mt-3 text-[14px] leading-relaxed text-white/60">
                    {p.desc}
                  </p>
                </div>
              );
            })}
          </div>
        </div>

        {/* Animated metric strip — subtle, blends with dark bg */}
        <div
          data-testid="omnia-marquee"
          aria-hidden="true"
          className="ihm-marquee-pause relative overflow-hidden border-t border-white/10 bg-[#1D1D1F] py-7 select-none"
        >
          <div className="pointer-events-none absolute inset-y-0 left-0 w-32 bg-gradient-to-r from-[#1D1D1F] to-transparent z-10" />
          <div className="pointer-events-none absolute inset-y-0 right-0 w-32 bg-gradient-to-l from-[#1D1D1F] to-transparent z-10" />
          <div className="ihm-marquee">
            {Array.from({ length: 2 }).map((_, dup) => (
              <div key={dup} className="flex items-center shrink-0">
                {[
                  "EdTech",
                  "AI Systems",
                  "Wearables",
                  "Ambient Intelligence",
                  "2026",
                  "Engineered to Scale",
                  "Future-facing",
                  "Built for the Decade Ahead",
                ].map((t, i) => (
                  <span
                    key={`${dup}-${i}`}
                    className="flex items-center gap-8 px-8 font-heading font-semibold text-2xl md:text-3xl tracking-tight text-white/30"
                  >
                    {t}
                    <span className="inline-block h-1.5 w-1.5 rounded-full bg-[#2997FF]/60" />
                  </span>
                ))}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section>
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-24">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-8 border-t border-[#E5E5EA] pt-16">
            <h3 className="font-heading font-semibold text-3xl md:text-4xl tracking-tight max-w-2xl">
              Want to learn more or partner with us?
            </h3>
            <Link
              to="/contact"
              data-testid="ventures-contact-cta"
              className="inline-flex items-center gap-3 rounded-full bg-[#1D1D1F] px-7 py-4 text-[15px] font-medium text-white transition-all duration-500 hover:bg-[#2997FF]"
            >
              Get in touch
              <ArrowUpRight size={18} />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
